import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

export interface SolarSummaryColumn<Row> {
  key: string;

  header: string;

  /** @default 'left' */
  align?: 'left' | 'right' | 'center';

  render: (row: Row) => ReactNode;
}

interface Props<Row> {
  columns: SolarSummaryColumn<Row>[];

  rows: Row[];

  getRowKey: (row: Row) => string;

  /** Cells of a bold closing row, one per column. */
  footer?: ReactNode[];

  caption?: string;
}

/** Compact static table for a handful of summary rows. */
const SolarSummaryTable = <Row,>({
  columns,
  rows,
  getRowKey,
  footer,
  caption,
}: Props<Row>) => (
  <>
    <Table size='small'>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell
              key={column.key}
              align={column.align}
              sx={{ fontWeight: 600, color: 'primary.main', py: 0.75 }}
            >
              {column.header}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={getRowKey(row)}>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align}
                sx={{ py: 0.75 }}
              >
                {column.render(row)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {footer && (
        <TableFooter>
          <TableRow>
            {footer.map((cell, i) => (
              <TableCell
                key={columns[i]?.key ?? i}
                align={columns[i]?.align}
                sx={{
                  fontWeight: 700,
                  fontSize: 'inherit',
                  color: 'text.primary',
                  py: 0.75,
                }}
              >
                {cell}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      )}
    </Table>
    {caption && (
      <Typography
        variant='caption'
        color='text.secondary'
        display='block'
        mt={1}
      >
        {caption}
      </Typography>
    )}
  </>
);

export default SolarSummaryTable;
